import { useTranslation } from 'react-i18next'
import React, { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Layout, MenuProps, notification, Typography } from 'antd'
import { colorText } from 'src/app/themes/primaryThemeDefault'
import { PenIcon, TimerHistoryIcon } from 'src/assets/icons'
import { menuUrls } from 'src/app/shared/constants/menu.constants'

import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import { IReqHelpPagePayload, useHelpPage } from '../useHelpPage'
import HelpHeader from '../components/help-header'
import { STATUS_STRING, StringVersionStatusTag } from '../components/TagComponents'
import dayjs from 'dayjs'
import { FORMAT_DATE } from '../../../shared/constants/app.constants'
import { useRolePermission } from '../../../shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from '../../../shared/models/roleAndPermission.model'
import '../index.scss'
import { getHelpPageList } from '../../../shared/services/HelpPageService'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'

const LandingHelpPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = location

  const { helpPageList, totalRecords, isLoading, getHelpPageListData } = useHelpPage()

  const [api, contextHolder] = notification.useNotification()
  const currentPayload = useRef<IReqHelpPagePayload>()
  const { userPermissions } = useRolePermission()

  const getItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        userPermissions[ESubModules.HELP_PAGE_MANAGE]?.includes(EPermissions.ADD_EDIT_ITEMS)
          ? {
              key: '1',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>{t(`HelpPage:list.label.editHelpPage`)}</Typography.Text>
              ),
              icon: <PenIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.helpPageManagement}/edit/${rowData?.id}`)
              },
            }
          : null,
        {
          key: '2',
          label: (
            <Typography.Text style={{ fontSize: '14px', color: colorText }}>
              {t(`HelpPage:list.label.viewVersionHistory`)}
            </Typography.Text>
          ),
          icon: <TimerHistoryIcon />,
          onClick: () => {
            if (rowData?.id)
              navigate(
                `${menuUrls.helpPageManagement}/view-version-history/${
                  rowData?.parentId ? rowData?.parentId : rowData?.id
                }`,
              )
          },
        },
      ]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps

    [t, navigate],
  )

  const generateCustomColumnDefs = useCallback((columnDefs: Array<TableColumnDefs>): Array<TableColumnDefs> => {
    return columnDefs.map((item: TableColumnDefs, index: any) => {
      if (item.colId === 'pageName')
        return {
          ...item,
          width: 150,
          headerClass: 'custom-header-text',
          cellRenderer: (props: any) => {
            const { data } = props
            return data.pageName
          },
        } as any
      if (item.colId === 'updatedby')
        return {
          ...item,
          width: 150,
          headerClass: 'custom-header-text',
          cellRenderer: (props: any) => {
            const { data } = props
            return data.updatedBy
          },
        } as any

      if (item.colId === 'url')
        return {
          ...item,
          width: 150,
          headerClass: 'custom-header-text',
          cellRenderer: (props: any) => {
            const { data } = props
            return (
              <Typography.Text
                style={{
                  display: 'block',
                  color: '#004B91',
                  fontWeight: 400,
                  fontSize: 14,
                  textDecoration: 'underline',
                }}
              >
                {data.url}
              </Typography.Text>
            )
          },
        } as any

      if (item.colId === 'versionStatus')
        return {
          ...item,
          width: 150,
          cellRenderer: (props: any) => {
            const { data } = props
            return <StringVersionStatusTag status={data.versionStatus} />
          },
        } as any

      if (item.colId === 'scheduledOn')
        return {
          ...item,
          width: 150,
          cellRenderer: (props: any) => {
            const { data } = props
            return data.scheduledOn && data.versionStatus === STATUS_STRING.SCHEDULED
              ? dayjs(data.scheduledOn).format(FORMAT_DATE)
              : ''
          },
        } as any

      if (item.colId === 'lastUpdateOn')
        return {
          ...item,
          width: 150,
          cellRenderer: (props: any) => {
            const { data } = props

            return data.lastUpdateOn
          },
        } as any

      return item
    })
  }, [])

  const handleAddRecord = () => {
    navigate(`${menuUrls.helpPageManagement}/add`)
  }

  const handleSearch = useCallback(async (payload: Partial<any>) => {
    currentPayload.current = payload
    await getHelpPageListData(payload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (state?.helpPageManagement?.message) {
      api.success({
        message: <RichTextField richText={state?.helpPageManagement?.message} />,
      })
    }
    window.history.replaceState({}, '')
  }, [api, state?.helpPageManagement])

  return (
    <Layout className={'help-page-management-screen'}>
      <HelpHeader title={t(`HelpPage:list.label.helpPageManagement`)} isShowBackIcon={false} />

      <Layout.Content style={{ padding: 24 }}>
        {contextHolder}

        <NewCommonTable
          rowData={helpPageList}
          total={totalRecords}
          handleAddRecord={handleAddRecord}
          displayAddBtn={userPermissions[ESubModules.HELP_PAGE_MANAGE]?.includes(EPermissions.ADD_EDIT_ITEMS)}
          loading={isLoading}
          actionDropdownItems={getItems}
          handleSearch={handleSearch}
          handleDownload={async (payload: any) => await getHelpPageList(payload)}
          pageName={TABLE_NAME.HELP_PAGE}
          translationNamespace='HelpPage:list.tableColumns'
          autoSort={false}
          tableSettingName={TABLE_NAME.HELP_PAGE}
          generateCustomColumnDefs={generateCustomColumnDefs}
        />
      </Layout.Content>
    </Layout>
  )
}

export default LandingHelpPage
